import { InteractionRequiredAuthError } from "@azure/msal-browser"
import { captureException } from "@sentry/react"
import type { HttpClient } from "@/shared/types/HttpClient"
import { logger } from "@/shared/utils/add/logger/logger"
import { toCamelCasePropertiesDeep } from "@/shared/utils/toCamelCasePropertiesDeep/toCamelCasePropertiesDeep"
import { toSnakeCasePropertiesDeep } from "@/shared/utils/toSnakeCasePropertiesDeep/toSnakeCasePropertiesDeep"
import type { TokenProvider } from "../auth/tokenProvider"
import { ApiError } from "../errors/ApiError"
import { AuthorizationError } from "../errors/AuthorizationError"
import { ConnectivityError } from "../errors/ConnectivityError"
import { addParamsToUrl } from "./urlUtils"

const NEVER_RETURN_VALUE = undefined as unknown as never
const NETWORK_ERRORS_REGEX = /failed to fetch|networkerror|load failed/iu

export type SessionRecoveryHandler = () => Promise<void>

/**
 * Unified HTTP client that works with any authentication method
 */
export function createHttpClient(tokenProvider: TokenProvider, sessionRecovery?: SessionRecoveryHandler): HttpClient {
  const makeRequest = async <T>(
    {
      url,
      method,
      data,
      params,
      redirectToLogout = true
    }: {
      url: string
      method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
      data?: unknown
      params?: Record<string, string>
      redirectToLogout?: boolean
    },
    isRetry = false
  ): Promise<T> => {
    const finalUrl = params ? addParamsToUrl(url, params) : url

    try {
      // Build headers
      const headers: Record<string, string> = {
        Accept: "application/json"
      }

      // Add Content-Type for requests with body
      if (method !== "GET" && data) {
        headers["Content-Type"] = "application/json"
      }

      // Add authorization header if token is required
      if (tokenProvider.requiresToken()) {
        const authHeader = await tokenProvider.getAuthorizationHeader()
        if (authHeader) {
          headers.Authorization = authHeader
        }
      }

      const config: RequestInit = {
        method,
        headers,
        credentials: tokenProvider.requiresToken() ? "omit" : "include",
        ...(data ? { body: JSON.stringify(toSnakeCasePropertiesDeep(data)) } : {})
      }

      const response = await fetch(finalUrl, config)
      const contentType = response.headers.get("content-type")
      const isJSONResponse = contentType?.includes("application/json")

      if (!response.ok) {
        if (response.status === 401 && redirectToLogout && !tokenProvider.requiresToken()) {
          const { redirectToLogin } = await import("@/utils/redirectToLogin")
          redirectToLogin()
          return NEVER_RETURN_VALUE
        }

        const rawBody = isJSONResponse ? await response.json() : await response.text()
        const body = isJSONResponse && typeof rawBody === "object" ? toCamelCasePropertiesDeep(rawBody) : rawBody

        const apiError = new ApiError({
          message: `HTTP ${method} request failed with status ${response.status.toString()}: ${response.statusText}`,
          status: response.status,
          statusText: response.statusText,
          url: finalUrl,
          body,
          method
        })

        if (response.status === 403) {
          const authError = new AuthorizationError(apiError)
          captureException(authError)
          throw authError
        }

        captureException(apiError)
        throw apiError
      }

      if (isJSONResponse) {
        return (await response.json().then(toCamelCasePropertiesDeep)) as T
      }
      return NEVER_RETURN_VALUE
    } catch (error: unknown) {
      // Handle stale MSAL sessions - show recovery modal and retry once
      if (!isRetry && sessionRecovery && error instanceof InteractionRequiredAuthError) {
        logger.debug("Session expired, triggering recovery flow")
        await sessionRecovery()
        return makeRequest({ url, method, data, params, redirectToLogout }, true)
      }

      logger.error(`HTTP ${method} ${finalUrl} failed`, error)

      // Handle complete network failures
      if (error instanceof TypeError && NETWORK_ERRORS_REGEX.test(error.message)) {
        throw new ConnectivityError(error)
      }

      throw error
    }
  }

  const client: HttpClient = {
    get: async <T>(url: string, params: Record<string, string> = {}) => makeRequest<T>({ url, method: "GET", params }),
    getWithoutRedirect: async <T>(url: string, params: Record<string, string> = {}) =>
      makeRequest<T>({ url, method: "GET", params, redirectToLogout: false }),
    post: async <T>(url: string, data: unknown) => makeRequest<T>({ url, method: "POST", data }),
    put: async <T>(url: string, data: unknown) => makeRequest<T>({ url, method: "PUT", data }),
    patch: async <T>(url: string, data: unknown) => makeRequest<T>({ url, method: "PATCH", data }),
    delete: async <T>(url: string) => makeRequest<T>({ url, method: "DELETE" })
  }

  return client
}
